import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { confirmPayment } from '../../../api/payments';
import { createWebCreatorClickCollectOrder } from '../../../api/webCreator';
import StancerPaymentModal from '../../../components/hubup/StancerPaymentModal';
import { formatPriceTtc } from '../utils/commerce';
import {
  clearClickCollectCart,
  loadClickCollectCart,
  loadClickCollectCustomer,
  loadClickCollectPickup,
  sanitizeCartForAvailableItems,
  saveClickCollectCart,
  saveClickCollectCustomer,
  saveClickCollectPickup,
} from '../utils/clickCollectCheckoutStorage';
import {
  formatReservationSlot,
  hasConfiguredPickupWeek,
  isDaySlotActive,
  weekdayKeyFromDate,
} from '../utils/commerceAdmin';
import {
  buildReservationTimeSlots,
  commerceDisplayColor,
  filterPickupTimeSlotsForDate,
  findEarliestPickupSlot,
  formatPreferredDateIso,
  groupMenuItemsByCategory,
  normalizeMenuTheme,
  parseMenuFooterLines,
  resolveClickCollectDisplay,
} from '../utils/commerceDisplay';
import ClickCollectRestaurantMenu from './ClickCollectRestaurantMenu';
import CommerceMonthCalendar from './CommerceMonthCalendar';

/** Calendrier et créneaux click & collect (aligné kit Jardins / réservation). */
const CLICK_COLLECT_SCHEDULE_COLOR = '#203B2D';

export default function ClickCollectPublicView({
  slug,
  site,
  siteTitle,
  items = [],
  display: displayOverride,
  onBack,
  onOrderComplete,
  embedded = false,
  interactive = true,
  pageHeaderLabel,
}) {
  const display = useMemo(() => {
    if (displayOverride) {
      return { ...resolveClickCollectDisplay(site || {}), ...displayOverride };
    }
    return resolveClickCollectDisplay(site || { title: siteTitle, clickAndCollect: { items } });
  }, [site, siteTitle, items, displayOverride]);

  const grouped = useMemo(() => groupMenuItemsByCategory(items), [items]);

  const [cart, setCart] = useState({});
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const [pickupDate, setPickupDate] = useState(null);
  const [pickupSlot, setPickupSlot] = useState('');
  const [stancerPayment, setStancerPayment] = useState(null);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [checkoutRestored, setCheckoutRestored] = useState(false);

  const flatAvailable = useMemo(
    () => grouped.flatMap(([, list]) => list),
    [grouped],
  );

  const lines = useMemo(() => {
    return Object.entries(cart)
      .map(([itemId, qty]) => {
        const item = flatAvailable.find((i) => i.id === itemId);
        if (!item || qty <= 0) return null;
        return { item, quantity: qty };
      })
      .filter(Boolean);
  }, [cart, flatAvailable]);

  const total = lines.reduce(
    (sum, { item, quantity }) => sum + (Number(item.priceTtc) || 0) * quantity,
    0,
  );

  const availableIds = useMemo(
    () => flatAvailable.map((i) => i.id),
    [flatAvailable],
  );

  useEffect(() => {
    if (!slug || checkoutRestored) return;
    const customer = loadClickCollectCustomer(slug);
    if (customer) {
      setName(customer.name);
      setEmail(customer.email);
      setPhone(customer.phone);
      setNote(customer.note);
    }
    const savedCart = loadClickCollectCart(slug);
    if (savedCart && Object.keys(savedCart).length) {
      setCart(sanitizeCartForAvailableItems(savedCart, availableIds));
    }
    const savedPickup = loadClickCollectPickup(slug);
    if (savedPickup) {
      setPickupDate(savedPickup.date);
      setPickupSlot(savedPickup.slot);
    }
    setCheckoutRestored(true);
  }, [slug, checkoutRestored, availableIds]);

  useEffect(() => {
    if (!slug || !checkoutRestored || done != null) return;
    saveClickCollectCustomer(slug, { name, email, phone, note });
  }, [slug, name, email, phone, note, checkoutRestored, done]);

  useEffect(() => {
    if (!slug || !checkoutRestored || done != null) return;
    saveClickCollectCart(slug, cart);
  }, [slug, cart, checkoutRestored, done]);

  useEffect(() => {
    if (!slug || !checkoutRestored || done != null) return;
    if (pickupDate && pickupSlot) {
      saveClickCollectPickup(slug, pickupDate, pickupSlot);
    }
  }, [slug, pickupDate, pickupSlot, checkoutRestored, done]);

  const setQty = useCallback((itemId, delta, max) => {
    setCart((prev) => {
      const cur = Number(prev[itemId]) || 0;
      const next = Math.max(0, Math.min(max, cur + delta));
      if (next === 0) {
        const { [itemId]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [itemId]: next };
    });
  }, []);

  const addOne = useCallback(
    (itemId, max) => {
      setQty(itemId, 1, max);
    },
    [setQty],
  );

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!interactive || !slug) {
      setError('Commande disponible sur le site publié.');
      return;
    }
    if (!lines.length) {
      setError('Ajoutez au moins un article.');
      return;
    }
    if (!pickupDate || !pickupSlot) {
      setError('Choisissez un créneau de retrait.');
      return;
    }
    const pickupAt = formatPreferredDateIso(pickupDate, pickupSlot);
    if (!pickupAt) {
      setError('Créneau de retrait invalide.');
      return;
    }
    setBusy(true);
    try {
      const res = await createWebCreatorClickCollectOrder(slug, {
        customerName: name.trim(),
        customerEmail: email.trim(),
        customerPhone: phone.trim(),
        pickupNote: note.trim(),
        pickupAt,
        lines: lines.map(({ item, quantity }) => ({ itemId: item.id, quantity })),
      });
      if (res?.data?.paymentUrl && interactive) {
        if (res?.data?.confirmationToken) {
          setStancerPayment({
            paymentUrl: res.data.paymentUrl,
            paymentId: res.data.paymentId,
            confirmationToken: res.data.confirmationToken,
            totalTtc: res?.data?.totalTtc ?? total,
          });
          return;
        }
        window.location.assign(res.data.paymentUrl);
        return;
      }
      const totalTtc = res?.data?.totalTtc ?? total;
      setDone(totalTtc);
      setCart({});
      clearClickCollectCart(slug);
      onOrderComplete?.();
    } catch (err) {
      setError(err?.message || 'Commande impossible.');
    } finally {
      setBusy(false);
    }
  };

  const accent = commerceDisplayColor(display.accentColor, '#432728');
  const btnBg = commerceDisplayColor(display.orderButtonColor, accent);
  const btnText = commerceDisplayColor(display.orderButtonTextColor, '#ffffff');
  const layout = display.layout === 'gallery' ? 'gallery' : 'classic';
  const pickupWeek = site?.clickAndCollect?.pickupWeek;
  const pickupConfigured = hasConfiguredPickupWeek(pickupWeek);
  const allTimeSlots = useMemo(() => buildReservationTimeSlots(), []);
  const minPickupDate = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const earliestPickup = useMemo(
    () => (pickupConfigured ? findEarliestPickupSlot(pickupWeek) : null),
    [pickupWeek, pickupConfigured],
  );

  const isPickupDayDisabled = useCallback(
    (date) => {
      if (!pickupConfigured) return true;
      const daySlot = pickupWeek?.[weekdayKeyFromDate(date)];
      if (!isDaySlotActive(daySlot)) return true;
      const slots = filterPickupTimeSlotsForDate(allTimeSlots, date, daySlot);
      return slots.length === 0;
    },
    [pickupWeek, pickupConfigured, allTimeSlots],
  );

  const availablePickupSlots = useMemo(() => {
    if (!pickupDate || !pickupConfigured) return [];
    const daySlot = pickupWeek?.[weekdayKeyFromDate(pickupDate)];
    return filterPickupTimeSlotsForDate(allTimeSlots, pickupDate, daySlot);
  }, [pickupDate, pickupWeek, pickupConfigured, allTimeSlots]);

  useEffect(() => {
    if (!earliestPickup) return;
    setPickupDate((prev) => prev ?? earliestPickup.date);
    setPickupSlot((prev) => prev || earliestPickup.slot);
  }, [earliestPickup]);

  useEffect(() => {
    if (pickupSlot && !availablePickupSlots.includes(pickupSlot)) {
      setPickupSlot(availablePickupSlots[0] || '');
    }
  }, [pickupSlot, availablePickupSlots]);

  const selectEarliestPickup = useCallback(() => {
    if (!earliestPickup) return;
    setPickupDate(earliestPickup.date);
    setPickupSlot(earliestPickup.slot);
    setError('');
  }, [earliestPickup]);

  const pickupReady = Boolean(pickupDate && pickupSlot && earliestPickup);
  const paymentEnabled = site?.clickAndCollect?.payment?.enabled === true;
  const cartAsideRef = useRef(null);
  const cartItemCount = lines.reduce((s, l) => s + l.quantity, 0);

  const scrollToCart = useCallback(() => {
    cartAsideRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handlePaymentAuthorized = useCallback(async () => {
    if (confirmingPayment || !stancerPayment?.confirmationToken) return;
    setConfirmingPayment(true);
    setError('');
    try {
      await confirmPayment(stancerPayment.confirmationToken);
      const totalTtc = stancerPayment.totalTtc ?? total;
      setStancerPayment(null);
      setDone(totalTtc);
      setCart({});
      clearClickCollectCart(slug);
      onOrderComplete?.();
    } catch (err) {
      setError(err?.message || 'Confirmation du paiement impossible.');
    } finally {
      setConfirmingPayment(false);
      setBusy(false);
    }
  }, [confirmingPayment, stancerPayment, total, slug, onOrderComplete]);

  const closeStancerModal = useCallback(() => {
    setStancerPayment(null);
    setBusy(false);
    setError('');
  }, []);

  const menuSubtitle = String(display.pageSubtitle || '').trim() || 'vous propose';
  const menuTheme = useMemo(() => normalizeMenuTheme(display.menuTheme), [display.menuTheme]);
  const footerLines = useMemo(
    () => parseMenuFooterLines(display.menuFooter).slice(0, 8),
    [display.menuFooter],
  );
  const pageBg = menuTheme.pageBackground || '#eae7e3';

  const rootClass = embedded ? 'w-full text-stone-900' : 'min-h-screen text-stone-900';

  return (
    <div className={rootClass} style={{ backgroundColor: pageBg }}>
      {!embedded ? (
        <header className="sticky top-0 z-30 border-b border-stone-300/60 bg-[#eae7e3]/95 backdrop-blur-md">
          <div className="max-w-[1200px] mx-auto px-4 py-3 relative flex items-center justify-center">
            <button
              type="button"
              className="absolute left-2 sm:left-4 p-2 rounded-lg hover:bg-white/60"
              onClick={onBack}
              aria-label="Retour"
            >
              <Icon name="ArrowLeft" size={20} />
            </button>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#432728] px-12">
              {pageHeaderLabel || 'Commande à emporter'}
            </p>
          </div>
        </header>
      ) : null}

      {!pickupConfigured || !earliestPickup ? (
        <div className={`${embedded ? 'w-full' : 'max-w-[1200px]'} mx-auto px-4 pt-4`}>
          <div className="rounded-lg bg-white/80 text-stone-600 px-4 py-3 text-sm text-center border border-stone-200">
            Aucun créneau de retrait configuré — contactez l&apos;établissement.
          </div>
        </div>
      ) : null}

      <div
        className={`click-collect-public ${embedded ? 'w-full max-w-none' : 'max-w-[1200px]'} mx-auto px-0 sm:px-4 ${embedded ? 'py-4 lg:py-6' : 'py-4 lg:py-8'} ${cartItemCount > 0 && !done ? 'pb-24 lg:pb-8' : ''}`}
      >
        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_min(100%,340px)] lg:gap-8 xl:gap-10 items-start">
          <div className="min-w-0 w-full">
            {grouped.length === 0 ? (
              <p className="text-sm text-stone-500 italic text-center py-16">
                Aucun article disponible pour le moment.
              </p>
            ) : layout === 'gallery' ? (
              <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
                {grouped.flatMap(([, catItems]) =>
                  catItems.map((item) => (
                    <MenuGalleryCard
                      key={item.id}
                      item={item}
                      accent={accent}
                      qty={Number(cart[item.id]) || 0}
                      onQty={(delta) =>
                        setQty(item.id, delta, Number(item.quantityAvailable) || 0)
                      }
                    />
                  )),
                )}
              </div>
            ) : (
              <ClickCollectRestaurantMenu
                title={display.pageTitle}
                subtitle={menuSubtitle}
                footerLines={footerLines}
                grouped={grouped}
                cart={cart}
                onAddItem={addOne}
                menuTheme={menuTheme}
              />
            )}
            <p className="text-center text-[11px] text-stone-500 mt-4 lg:hidden">
              Touchez un plat pour l&apos;ajouter au panier
            </p>
          </div>

          <aside
            ref={cartAsideRef}
            className="mt-8 mx-3 sm:mx-0 lg:mt-0 lg:sticky lg:top-[4.5rem] rounded-2xl border border-stone-200 bg-white shadow-lg overflow-hidden z-20 scroll-mt-20"
          >
            <div className="px-5 py-4 border-b border-stone-100 bg-[#432728]/5">
              <h3
                className="text-lg font-semibold text-[#432728]"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Votre panier
              </h3>
              {lines.length ? (
                <p className="text-xs text-stone-500 mt-0.5">
                  {lines.reduce((s, l) => s + l.quantity, 0)} article
                  {lines.reduce((s, l) => s + l.quantity, 0) > 1 ? 's' : ''}
                </p>
              ) : null}
            </div>
            <div className="p-5 space-y-4 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto">
              {done != null ? (
                <p
                  className="text-sm rounded-xl p-4 text-[#432728]"
                  style={{ backgroundColor: 'rgba(67,39,40,0.08)' }}
                >
                  Merci {name ? `${name.split(' ')[0]}` : ''} ! Commande enregistrée (
                  {formatPriceTtc(done)} TTC).
                  {pickupDate && pickupSlot
                    ? ` Retrait prévu ${formatReservationSlot(
                        formatPreferredDateIso(pickupDate, pickupSlot),
                      )}.`
                    : ' Retrait à confirmer prochainement.'}
                </p>
              ) : (
                <form onSubmit={submit} className="space-y-3">
                  {pickupConfigured && earliestPickup ? (
                    <div className="space-y-3 border-b border-stone-100 pb-3">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-semibold text-stone-700 uppercase tracking-wide">
                          Créneau de retrait
                        </p>
                        <button
                          type="button"
                          className="text-xs font-semibold underline-offset-2 hover:underline disabled:opacity-40"
                          style={{ color: CLICK_COLLECT_SCHEDULE_COLOR }}
                          onClick={selectEarliestPickup}
                          disabled={!earliestPickup}
                        >
                          Au plus tôt
                        </button>
                      </div>
                      {pickupDate && pickupSlot ? (
                        <p className="text-[11px] text-stone-500 leading-snug">
                          {formatReservationSlot(formatPreferredDateIso(pickupDate, pickupSlot))}
                        </p>
                      ) : null}
                      <CommerceMonthCalendar
                        value={pickupDate}
                        onChange={(d) => {
                          setPickupDate(d);
                          setPickupSlot('');
                          setError('');
                        }}
                        minDate={minPickupDate}
                        accentColor={CLICK_COLLECT_SCHEDULE_COLOR}
                        isDayDisabled={isPickupDayDisabled}
                        className="click-collect-pickup-calendar"
                      />
                      {pickupDate ? (
                        <div className="space-y-2">
                          <p className="text-[10px] font-semibold text-stone-600">Horaire</p>
                          {availablePickupSlots.length === 0 ? (
                            <p className="text-xs text-stone-500">Aucun horaire ce jour.</p>
                          ) : (
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                              {availablePickupSlots.map((slot) => (
                                <button
                                  key={slot}
                                  type="button"
                                  onClick={() => setPickupSlot(slot)}
                                  className={`py-2 rounded-lg text-xs font-medium border transition-colors ${
                                    pickupSlot === slot
                                      ? 'text-white border-transparent shadow-sm'
                                      : 'border-stone-200 bg-white text-stone-800 hover:border-stone-300'
                                  }`}
                                  style={
                                    pickupSlot === slot
                                      ? {
                                          backgroundColor: CLICK_COLLECT_SCHEDULE_COLOR,
                                          borderColor: CLICK_COLLECT_SCHEDULE_COLOR,
                                        }
                                      : undefined
                                  }
                                >
                                  {slot}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  {lines.length ? (
                    <ul className="text-sm space-y-2 border-b border-stone-100 pb-3">
                      {lines.map(({ item, quantity }) => (
                        <li key={item.id} className="flex justify-between gap-2 items-start">
                          <span className="text-stone-700 min-w-0">
                            <span className="font-semibold tabular-nums">{quantity}×</span>{' '}
                            {item.title}
                          </span>
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              type="button"
                              className="h-7 w-7 rounded-md border border-stone-200 text-sm"
                              onClick={() =>
                                setQty(
                                  item.id,
                                  -1,
                                  Number(item.quantityAvailable) || 0,
                                )
                              }
                              aria-label="Retirer"
                            >
                              −
                            </button>
                            <span className="font-medium tabular-nums w-14 text-right">
                              {formatPriceTtc(item.priceTtc * quantity)}
                            </span>
                          </div>
                        </li>
                      ))}
                      <li className="flex justify-between font-semibold pt-2 text-base border-t border-dashed border-stone-200">
                        <span>Total TTC</span>
                        <span className="text-[#432728]">{formatPriceTtc(total)}</span>
                      </li>
                    </ul>
                  ) : (
                    <p className="text-sm text-stone-500 py-6 text-center leading-relaxed">
                      Cliquez sur un plat dans la carte pour commencer votre commande.
                    </p>
                  )}
                  <label className="block text-xs font-medium text-stone-600">
                    Nom complet *
                    <input
                      className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#432728]/20"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Jean Dupont"
                      autoComplete="name"
                    />
                  </label>
                  <input
                    type="email"
                    className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm"
                    placeholder="E-mail *"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                  <input
                    type="tel"
                    className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm"
                    placeholder="Téléphone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    autoComplete="tel"
                  />
                  <textarea
                    className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm min-h-[72px]"
                    placeholder="Remarques (allergies, heure de retrait…)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                  {error ? <p className="text-xs text-red-600">{error}</p> : null}
                  <button
                    type="submit"
                    disabled={busy || !pickupReady || !lines.length || !interactive}
                    className="w-full rounded-lg py-3 text-sm font-semibold shadow-md transition-opacity disabled:opacity-50"
                    style={{ backgroundColor: btnBg, color: btnText }}
                  >
                    {busy
                      ? 'Envoi…'
                      : pickupReady
                        ? paymentEnabled
                          ? 'Payer ma commande'
                          : 'Valider la commande'
                        : 'Choisissez un créneau'}
                  </button>
                </form>
              )}
            </div>
          </aside>
        </div>
      </div>

      {stancerPayment ? (
        <StancerPaymentModal
          iframeUrl={stancerPayment.paymentUrl}
          paymentId={stancerPayment.paymentId}
          overlayVariant="jardins"
          onAuthorized={handlePaymentAuthorized}
          onError={(msg) => {
            setError(msg);
            closeStancerModal();
          }}
          onClose={closeStancerModal}
        />
      ) : null}

      {confirmingPayment ? (
        <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-[#EAE7E3] backdrop-blur-md">
          <div className="rounded-xl bg-white px-6 py-4 flex items-center gap-3 shadow-lg border border-stone-200">
            <Icon name="Loader2" size={20} className="animate-spin text-[#203B2D]" />
            <span className="text-sm text-stone-800">Confirmation du paiement…</span>
          </div>
        </div>
      ) : null}

      {cartItemCount > 0 && !done && !stancerPayment ? (
        <div
          className="lg:hidden fixed bottom-0 inset-x-0 z-50 border-t border-stone-200 bg-white/95 backdrop-blur-md px-4 py-3 shadow-[0_-8px_24px_rgba(0,0,0,0.08)]"
          style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            onClick={scrollToCart}
            className="w-full flex items-center justify-between gap-3 rounded-xl py-3.5 px-4 text-sm font-semibold shadow-md"
            style={{ backgroundColor: btnBg, color: btnText }}
          >
            <span>
              Voir le panier · {cartItemCount} article{cartItemCount > 1 ? 's' : ''}
            </span>
            <span className="tabular-nums">{formatPriceTtc(total)}</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}

function MenuGalleryCard({ item, accent, qty, onQty }) {
  const max = Number(item.quantityAvailable) || 0;
  const cover = (item.imageUrls || [])[0];
  return (
    <article
      className="rounded-2xl border border-stone-200 bg-white overflow-hidden shadow-sm flex flex-col cursor-pointer"
      onClick={() => max > 0 && qty < max && onQty(1)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && max > 0 && qty < max) onQty(1);
      }}
      role="button"
      tabIndex={0}
    >
      {cover ? (
        <img src={cover} alt="" className="h-40 w-full object-cover bg-stone-100" />
      ) : (
        <div
          className="h-32 flex items-center justify-center text-stone-400"
          style={{ backgroundColor: `${accent}08` }}
        >
          <Icon name="UtensilsCrossed" size={28} />
        </div>
      )}
      <div className="p-4 flex flex-col flex-1 gap-2">
        <div className="flex justify-between gap-2 items-start">
          <h3 className="font-serif font-semibold text-lg leading-tight">{item.title}</h3>
          <span className="font-semibold shrink-0" style={{ color: accent }}>
            {formatPriceTtc(item.priceTtc)}
          </span>
        </div>
        {item.description ? (
          <p className="text-sm text-stone-600 line-clamp-3 flex-1">{item.description}</p>
        ) : null}
        <div className="flex items-center justify-between pt-2">
          <span className="text-xs text-stone-500">{qty > 0 ? `${qty} au panier` : `${max} dispo.`}</span>
          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="h-8 w-8 rounded-lg border border-stone-200"
              onClick={() => onQty(-1)}
            >
              −
            </button>
            <span className="w-6 text-center tabular-nums">{qty}</span>
            <button
              type="button"
              className="h-8 w-8 rounded-lg text-white disabled:opacity-40"
              style={{ backgroundColor: accent }}
              disabled={qty >= max}
              onClick={() => onQty(1)}
            >
              +
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}
